import { useEffect, useLayoutEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  ChevronRight,
  Plus,
  CalendarDays,
  Check,
  RefreshCw,
} from "lucide-react";
import { money, dateLabel, londonToday } from "../../shared/domain";
import {
  overviewSearch,
  periodRange,
  type OverviewData,
  type OverviewQuery,
  type ReportRow,
  type ReportScope,
} from "../../shared/overview";
import { api, errorMessage } from "../api";
import { Field, Notice, Spinner } from "./Common";

const scopes: Record<ReportScope, string> = {
  sales: "Invoiced sales",
  received: "Payments received",
  outstanding: "Outstanding now",
  overdue: "Overdue now",
  undated: "Payments without a date",
};
function rangeLabel(q: OverviewQuery, r: { from: string; to: string }) {
  if (q.period === "all") return "All time";
  if (q.period === "month")
    return new Date(r.from + "T12:00:00Z").toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  if (q.period === "year") return r.from.slice(0, 4);
  return `${dateLabel(r.from, true)} – ${dateLabel(r.to, true)}`;
}
function overdueDays(due: string, today: string) {
  return Math.max(
    0,
    Math.round(
      (Date.parse(today + "T12:00:00Z") - Date.parse(due + "T12:00:00Z")) /
        86400000,
    ),
  );
}
export function Overview({
  query,
  revision,
  onQuery,
  onDocument,
  onNew,
  onReady,
  onReportBack,
}: {
  query: OverviewQuery;
  revision: number;
  onQuery: (q: OverviewQuery) => void;
  onDocument: (id: string) => void;
  onNew: () => void;
  onReady: () => void;
  onReportBack: () => void;
}) {
  const [data, setData] = useState<OverviewData | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [attempt, setAttempt] = useState(0),
    [custom, setCustom] = useState(false);
  const [from, setFrom] = useState(
      query.from || londonToday().slice(0, 7) + "-01",
    ),
    [to, setTo] = useState(query.to || londonToday()),
    [dateError, setDateError] = useState("");
  const params = overviewSearch({
    ...query,
    scope: undefined,
    client: undefined,
    clientName: undefined,
  });
  useEffect(() => {
    if (query.scope) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    void api<OverviewData>(`/overview?${params}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params, revision, attempt, query.scope]);
  useLayoutEffect(() => {
    if (!loading && !query.scope) onReady();
  }, [loading, query.scope, onReady]);
  const open = (scope: ReportScope) =>
    onQuery({ ...query, scope, client: undefined, clientName: undefined });
  if (query.scope)
    return (
      <Report
        onReady={onReady}
        query={query}
        revision={revision}
        onBack={onReportBack}
        onDocument={onDocument}
      />
    );
  return (
    <div className="overview-page">
      <div className="page-title overview-title">
        <div>
          <h1>Overview</h1>
          <p className="muted">Your business, by the numbers.</p>
        </div>
        <button className="secondary inline overview-new" onClick={onNew}>
          <Plus size={18} />
          <span>New invoice</span>
        </button>
      </div>
      <div
        className="overview-period"
        role="group"
        aria-label="Reporting period"
      >
        {(["month", "year", "all"] as const).map((p) => (
          <button
            key={p}
            aria-pressed={query.period === p && !custom}
            onClick={() => {
              setCustom(false);
              onQuery({ period: p });
            }}
          >
            {p === "month"
              ? "This month"
              : p === "year"
                ? "This year"
                : "All time"}
          </button>
        ))}
        <button
          aria-pressed={custom || query.period === "custom"}
          aria-expanded={custom}
          aria-controls="overview-dates"
          onClick={() => {
            setCustom(!custom);
            setDateError("");
          }}
        >
          <CalendarDays size={16} />
          <span>Custom</span>
        </button>
      </div>
      {custom && (
        <form
          id="overview-dates"
          className="overview-dates"
          onSubmit={(e) => {
            e.preventDefault();
            try {
              periodRange({ period: "custom", from, to }, londonToday());
              setDateError("");
              setCustom(false);
              onQuery({ period: "custom", from, to });
            } catch (err) {
              setDateError(errorMessage(err));
            }
          }}
        >
          <Field label="From">
            <input
              type="date"
              required
              min="2000-01-01"
              max="2100-12-31"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              required
              min="2000-01-01"
              max="2100-12-31"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          <button className="primary">Apply dates</button>
          {dateError && <Notice type="error">{dateError}</Notice>}
        </form>
      )}
      {error && (
        <Notice type="error">
          {error}
          <button
            className="text-button"
            onClick={() => setAttempt((n) => n + 1)}
          >
            <RefreshCw size={16} />
            Try again
          </button>
        </Notice>
      )}
      {loading && (
        <div
          className="overview-skeleton"
          role="status"
          aria-label="Loading overview"
        >
          <span />
          <span />
          <span />
          <span />
        </div>
      )}
      {data && (
        <>
          <p className="overview-period-label">
            {rangeLabel(query, data.range)}
          </p>
          <section
            className="overview-headline"
            aria-label="Sales and payments"
          >
            <button className="sales-metric" onClick={() => open("sales")}>
              <span className="metric-label">
                Invoiced sales <ArrowUpRight size={19} />
              </span>
              <strong>{money(data.salesPence)}</strong>
              <span className="metric-note">
                Excluding VAT · {data.invoiceCount}{" "}
                {data.invoiceCount === 1 ? "invoice" : "invoices"}
              </span>
            </button>
            <div className="overview-cash">
              <button className="cash-metric" onClick={() => open("received")}>
                <span className="metric-label">
                  Payments received <ChevronRight size={17} />
                </span>
                <strong>
                  {money(
                    data.receivedPence +
                      (query.period === "all" ? data.undatedPence : 0),
                  )}
                </strong>
                <span className="metric-note">
                  Including VAT · recorded in the app
                </span>
              </button>
              {data.undatedPence > 0 && (
                <button
                  className="undated-link"
                  onClick={() => open("undated")}
                >
                  {money(data.undatedPence)} without a date{" "}
                  {query.period === "all" ? "included" : "not included"}{" "}
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
          </section>
          <section
            className="overview-balances"
            aria-label="Current balances across all invoices"
          >
            <button onClick={() => open("outstanding")}>
              <span className="metric-label">
                Outstanding now <ChevronRight size={16} />
              </span>
              <strong>{money(data.outstandingPence)}</strong>
              <span className="metric-note">
                {data.outstandingCount}{" "}
                {data.outstandingCount === 1 ? "invoice" : "invoices"} ·
                including VAT
              </span>
            </button>
            <button
              onClick={() => open("overdue")}
              className={data.overdueCount ? "has-overdue" : ""}
            >
              <span className="metric-label">
                Overdue now <ChevronRight size={16} />
              </span>
              <strong>{money(data.overduePence)}</strong>
              <span className="metric-note">
                {data.overdueCount}{" "}
                {data.overdueCount === 1 ? "invoice" : "invoices"} · including
                VAT
              </span>
            </button>
          </section>
          <SalesChart
            data={data}
            query={query}
            onSelect={(from, to) =>
              onQuery({ period: "custom", from, to, scope: "sales" })
            }
          />
          <div className="overview-lists">
            <section className="overview-section">
              <div className="overview-section-title">
                <h2>Top customers</h2>
                <span>Sales excluding VAT</span>
              </div>
              {data.customers.length ? (
                data.customers.map((c, i) => (
                  <button
                    className="overview-customer"
                    key={c.id}
                    onClick={() =>
                      onQuery({
                        ...query,
                        scope: "sales",
                        client: c.id,
                        clientName: c.name,
                      })
                    }
                  >
                    <span className="customer-position" aria-hidden="true">
                      {i + 1}
                    </span>
                    <span>
                      <strong>{c.name}</strong>
                      <small>
                        {c.count} {c.count === 1 ? "invoice" : "invoices"}
                      </small>
                    </span>
                    <span className="customer-value">
                      {money(c.netPence)}
                      <ChevronRight size={16} />
                    </span>
                  </button>
                ))
              ) : (
                <div className="overview-empty">
                  <p>No invoiced sales in this period.</p>
                  <button className="text-button" onClick={onNew}>
                    <Plus size={16} />
                    Create an invoice
                  </button>
                </div>
              )}
            </section>
            <section className="overview-section">
              <div className="overview-section-title">
                <h2>Needs attention</h2>
                {data.overdueCount > 5 && (
                  <button
                    className="text-button"
                    onClick={() => open("overdue")}
                  >
                    View all {data.overdueCount}
                  </button>
                )}
              </div>
              {data.overdue.length ? (
                data.overdue.map((d) => (
                  <ReportItem
                    key={d.id}
                    row={d}
                    onOpen={onDocument}
                    note={`${overdueDays(d.dueDate, data.today)} days overdue`}
                  />
                ))
              ) : (
                <div className="overview-empty overview-clear">
                  <Check size={20} />
                  <div>
                    <strong>No overdue invoices</strong>
                    <p>Nothing needs following up today.</p>
                  </div>
                </div>
              )}
            </section>
          </div>
          <details className="overview-basis">
            <summary>How these figures are calculated</summary>
            <div className="overview-guide">
              <dl className="overview-definitions">
                <div>
                  <dt>Invoiced sales</dt>
                  <dd>
                    The value of invoices you’ve issued, before VAT, whether or
                    not the customer has paid. The invoice date determines which
                    period it appears in.
                  </dd>
                </div>
                <div>
                  <dt>Payments received</dt>
                  <dd>
                    The money you’ve recorded as received, including VAT. Each
                    payment counts in the period of its payment date, even if
                    the invoice was issued earlier. Record payments on the
                    invoice to keep this figure up to date.
                  </dd>
                </div>
                <div>
                  <dt>Outstanding now</dt>
                  <dd>
                    Everything customers still owe across all issued invoices,
                    including VAT, after recorded payments are deducted. This
                    covers all dates, whichever period you select.
                  </dd>
                </div>
                <div>
                  <dt>Overdue now</dt>
                  <dd>
                    The part of your outstanding balance whose payment due date
                    has passed. It is already included in Outstanding now, so
                    don’t add the two figures together.
                  </dd>
                </div>
              </dl>
              <section
                className="overview-example"
                aria-labelledby="overview-example-title"
              >
                <h3 id="overview-example-title">A simple example</h3>
                <p>
                  You issue an invoice in September for{" "}
                  <strong>£320 + £64 VAT = £384</strong>. The customer pays{" "}
                  <strong>£100 in October</strong>, and you record that payment.
                </p>
                <ul>
                  <li>
                    <strong>September’s invoiced sales:</strong> £320
                  </li>
                  <li>
                    <strong>October’s payments received:</strong> £100
                  </li>
                  <li>
                    <strong>Outstanding now:</strong> £284, including VAT
                  </li>
                </ul>
                <p>
                  If the due date has passed, that same £284 also appears in
                  Overdue now. This example is illustrative; it doesn’t change
                  your records.
                </p>
              </section>
              <section
                className="overview-guide-notes"
                aria-labelledby="overview-notes-title"
              >
                <h3 id="overview-notes-title">What else to know</h3>
                <ul>
                  <li>
                    <strong>
                      VAT in your selected period: {money(data.vatPence)}.
                    </strong>{" "}
                    This is the VAT on the invoices counted in Invoiced sales.
                    It is shown separately from sales.
                  </li>
                  <li>
                    <strong>Only issued invoices count towards sales.</strong>{" "}
                    Drafts, quotations, pro formas and void invoices are
                    excluded. Paid invoices still count.
                  </li>
                  <li>
                    <strong>
                      Voiding an invoice removes its sales and unpaid balance.
                    </strong>{" "}
                    Payments already recorded stay in Payments received. Refunds
                    are not tracked here.
                  </li>
                  <li>
                    <strong>
                      Payments without a date appear in All time only.
                    </strong>{" "}
                    They can’t be assigned to a month or custom date range.
                  </li>
                  <li>
                    <strong>Dates follow UK time.</strong> Current balances are
                    calculated as of {dateLabel(data.today)}. This may differ
                    from the date on your phone if you’re in another time zone.
                  </li>
                  <li>
                    <strong>These figures come from this app’s records.</strong>{" "}
                    Payments aren’t checked against your bank, and business
                    expenses aren’t included, so this dashboard doesn’t
                    calculate profit.
                  </li>
                </ul>
              </section>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function ReportItem({
  row,
  onOpen,
  note,
}: {
  row: ReportRow;
  onOpen: (id: string) => void;
  note: string;
}) {
  return (
    <button className="overview-document" onClick={() => onOpen(row.id)}>
      <span>
        <strong>{row.clientName}</strong>
        <small className="document-number">{row.number}</small>
        <small className="report-note">{note}</small>
      </span>
      <span className="report-value">
        <strong>{money(row.amountPence)}</strong>
        <ChevronRight size={18} />
      </span>
    </button>
  );
}
function Report({
  query,
  revision,
  onBack,
  onDocument,
  onReady,
}: {
  query: OverviewQuery;
  revision: number;
  onBack: () => void;
  onDocument: (id: string) => void;
  onReady: () => void;
}) {
  const [rows, setRows] = useState<ReportRow[]>([]),
    [more, setMore] = useState(false),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const params = overviewSearch(query),
    scope = query.scope || "sales";
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError("");
    setRows([]);
    setMore(false);
    void api<{
      documents: ReportRow[];
      hasMore: boolean;
      range: { from: string; to: string };
    }>(`/overview/documents?${params}`)
      .then((d) => {
        if (!cancelled) {
          setRows(d.documents);
          setMore(d.hasMore);
          setRange(d.range);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params, revision, attempt]);
  useLayoutEffect(() => {
    if (!busy) onReady();
  }, [busy, onReady]);
  const moreRows = async () => {
    setBusy(true);
    setError("");
    try {
      const d = await api<{ documents: ReportRow[]; hasMore: boolean }>(
        `/overview/documents?${params}&offset=${rows.length}`,
      );
      setRows((old) => [...old, ...d.documents]);
      setMore(d.hasMore);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="overview-page overview-report">
      <button className="back" onClick={onBack}>
        <ArrowLeft size={18} />
        Overview
      </button>
      <div className="page-title">
        <div>
          <h1>{query.clientName || scopes[scope]}</h1>
          <p className="muted">
            {query.clientName ? "Invoiced sales · " : ""}
            {scope === "outstanding" || scope === "overdue"
              ? "Across all invoices"
              : scope === "undated"
                ? "Recorded amounts with no payment date"
                : range
                  ? rangeLabel(query, range)
                  : "Loading period…"}{" "}
            · {scope === "sales" ? "excluding" : "including"} VAT
          </p>
        </div>
      </div>
      {error && (
        <Notice type="error">
          {error}
          <button
            className="text-button"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Try again
          </button>
        </Notice>
      )}
      <div className="overview-report-list">
        {rows.map((row) => (
          <ReportItem
            key={row.id}
            row={row}
            onOpen={onDocument}
            note={
              scope === "overdue"
                ? `${overdueDays(row.dueDate, londonToday())} days overdue`
                : scope === "outstanding"
                  ? `Due ${dateLabel(row.dueDate, true)}`
                  : scope === "received"
                    ? row.paymentDate
                      ? `Last payment ${dateLabel(row.paymentDate, true)}`
                      : "Date not recorded"
                    : scope === "undated"
                      ? "Date not recorded"
                      : `Issued ${dateLabel(row.issueDate, true)}`
            }
          />
        ))}
      </div>
      {busy && (
        <div className="report-loading" role="status">
          <Spinner />
          Loading invoices…
        </div>
      )}
      {!busy && !error && !rows.length && (
        <div className="overview-empty">
          <h2>No invoices to show</h2>
          <p>There are no matching records for this view.</p>
        </div>
      )}
      {more && (
        <button
          className="secondary load-more"
          disabled={busy}
          onClick={() => void moreRows()}
        >
          Load more invoices
        </button>
      )}
    </div>
  );
}

function SalesChart({
  data,
  query,
  onSelect,
}: {
  data: OverviewData;
  query: OverviewQuery;
  onSelect: (from: string, to: string) => void;
}) {
  const start =
    query.period === "all"
      ? data.firstDate || data.today
      : data.chartRange.from;
  const end =
    query.period === "all" ? data.lastDate || data.today : data.chartRange.to;
  const count =
    (Number(end.slice(0, 4)) - Number(start.slice(0, 4))) * 12 +
    Number(end.slice(5, 7)) -
    Number(start.slice(5, 7)) +
    1;
  const yearly = count > 24;
  const values = new Map<string, number>();
  for (const m of data.months) {
    const key = yearly ? m.month.slice(0, 4) : m.month;
    values.set(key, (values.get(key) || 0) + m.netPence);
  }
  const points: {
    key: string;
    label: string;
    full: string;
    amount: number;
    from: string;
    to: string;
  }[] = [];
  const first = new Date(start + "T12:00:00Z");
  for (
    let i = 0;
    i <
    (yearly ? Number(end.slice(0, 4)) - Number(start.slice(0, 4)) + 1 : count);
    i++
  ) {
    const d = new Date(
      Date.UTC(
        first.getUTCFullYear() + (yearly ? i : 0),
        yearly ? 0 : first.getUTCMonth() + i,
        1,
      ),
    );
    const from = d.toISOString().slice(0, 10),
      to = new Date(
        Date.UTC(
          d.getUTCFullYear() + (yearly ? 1 : 0),
          yearly ? 0 : d.getUTCMonth() + 1,
          0,
        ),
      )
        .toISOString()
        .slice(0, 10);
    const key = from.slice(0, yearly ? 4 : 7);
    points.push({
      key,
      label: yearly
        ? key
        : d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }),
      full: yearly
        ? key
        : d.toLocaleDateString("en-GB", {
            month: "long",
            year: "numeric",
            timeZone: "UTC",
          }),
      amount: values.get(key) || 0,
      from: from < start ? start : from,
      to: to > end ? end : to,
    });
  }
  const max = Math.max(...points.map((p) => p.amount), 100);
  const [selected, setSelected] = useState<string | null>(null);
  const active = points.find((p) => p.key === selected);
  return (
    <section className="overview-section sales-chart">
      <div className="overview-section-title">
        <h2>{yearly ? "Yearly" : "Monthly"} sales</h2>
        <span>
          {query.period === "month" ? "Last 6 months · " : ""}Excluding VAT
        </span>
      </div>
      <div className="chart-readout" aria-live="polite">
        {active ? (
          <>
            <span>{active.full}</span>
            <strong>{money(active.amount)}</strong>
            <button
              className="text-button"
              onClick={() => onSelect(active.from, active.to)}
            >
              View invoices
              <ArrowUpRight size={16} />
            </button>
          </>
        ) : (
          <span>
            Tap a {yearly ? "year" : "month"} to explore its invoices.
          </span>
        )}
      </div>
      <div className="chart-scale">
        <span aria-label={money(max)}>
          {new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            notation: "compact",
            maximumFractionDigits: 1,
          }).format(max / 100)}
        </span>
        <span>£0</span>
      </div>
      <div className="chart-scroll">
        <div
          className="chart-columns"
          style={{
            minWidth: points.length * 44,
          }}
          role="group"
          aria-label={yearly ? "Sales by year" : "Sales by month"}
        >
          {points.map((p) => (
            <button
              key={p.key}
              className={`chart-column ${p.key === selected ? "selected" : ""} ${p.key === data.today.slice(0, yearly ? 4 : 7) ? "current" : ""}`}
              aria-label={`${p.full}: ${money(p.amount)} excluding VAT`}
              aria-pressed={selected === p.key}
              onClick={() => setSelected(p.key)}
            >
              <span className="chart-bar-track">
                <span
                  className="chart-bar"
                  style={{ height: `${(p.amount / max) * 100}%` }}
                />
                <span className="chart-zero" hidden={p.amount > 0} />
              </span>
              <span className="chart-month">{p.label}</span>
              {!yearly && (p.label === "Jan" || points[0] === p) && (
                <span className="chart-year">{p.key.slice(0, 4)}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
